import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { formatPricesInText } from '../../utils/priceFormatter';
import { PromotionalPrice } from '../PromotionalPrice/PromotionalPrice';
import { CampaignConditions } from '../CampaignConditions/CampaignConditions';
import { useCampaignPrice } from '../../hooks/useCampaignPrice';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import './style.less';

export const ServiceModal = ({ service, onClose }) => {
  const { t } = useTranslation();

  // Get campaign pricing for the service header
  const { hasDiscount, discountedPrice, discountPercent, discountNote } =
    useCampaignPrice(service?.id, service?.price);

  useEffect(() => {
    // Lock body scroll when modal is open
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Handle ESC key to close modal
  useEscapeKey(onClose);

  // Focus trap for accessibility
  useEffect(() => {
    const modalElement = document.querySelector('.service-modal');
    if (!modalElement) return;

    const focusableElements = modalElement.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (!focusableElements.length) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTabKey = (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement.focus();
          e.preventDefault();
        }
      }
    };

    modalElement.addEventListener('keydown', handleTabKey);
    firstElement.focus();

    return () => {
      modalElement.removeEventListener('keydown', handleTabKey);
    };
  }, []);

  if (!service) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="service-modal-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="service-modal"
          initial={{ opacity: 0, scale: 0.9, y: 50 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 50 }}
          transition={{ type: 'spring', duration: 0.5 }}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
        >
          <button
            className="modal-close"
            onClick={onClose}
            aria-label={t('serviceModal.close')}
            tabIndex={0}
          >
            <i className="fas fa-times"></i>
          </button>

          <div className="modal-content">
            <div className="modal-header">
              <div className="modal-icon">
                <i className={`fas ${service.icon}`}></i>
              </div>
              <h2 id="modal-title">{service.title}</h2>
            </div>

            <div className="modal-body">
              <div className="service-details">
                <div className="detail-item">
                  <i className="fas fa-tag"></i>
                  <PromotionalPrice
                    originalPrice={service.price}
                    discountedPrice={discountedPrice}
                    discountPercent={discountPercent}
                    showDiscount={hasDiscount}
                    size="medium"
                    discountNote={discountNote}
                  />
                </div>
                {service.duration && (
                  <div className="detail-item">
                    <i className="fas fa-clock"></i>
                    <span>{service.duration}</span>
                  </div>
                )}
              </div>

              {service.description && (
                <p className="service-description">{service.description}</p>
              )}

              {service.intro && <p className="service-intro">{service.intro}</p>}

              {service.includes && service.includes.length > 0 && (
                <div className="service-includes">
                  <h3>{t('serviceModal.includes')}</h3>
                  <ul>
                    {service.includes.map((item, index) => (
                      <li key={index}>
                        <i className="fas fa-check-circle"></i>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {service.priceDetails && (
                <div className="price-details-section">
                  <h3>{t('serviceModal.priceDetails')}</h3>
                  <p className="price-base">{formatPricesInText(service.priceDetails.base)}</p>
                  {service.priceDetails.additional &&
                    service.priceDetails.additional.length > 0 && (
                      <ul className="price-additional">
                        {service.priceDetails.additional.map((item, index) => (
                          <li key={index}>{formatPricesInText(item)}</li>
                        ))}
                      </ul>
                    )}
                  {service.priceDetails.note && (
                    <p className="price-note">{formatPricesInText(service.priceDetails.note)}</p>
                  )}
                </div>
              )}

              {service.priceNote && <p className="service-price-note">{service.priceNote}</p>}


              {/* Contact Button */}
              <div className="calculator-contact-section">
                <p className="contact-text">{t('serviceModal.readyToStart')}</p>
                <a
                  href={`/contact?service=${encodeURIComponent(service.title)}`}
                  className="btn-primary btn-contact"
                >
                  <i className="fas fa-envelope"></i> {t('serviceModal.contactUs')}
                </a>
              </div>

              {/* Campaign Conditions */}
              <CampaignConditions />
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
